import { ElementList, Policy } from "@design-controls/types";
import { PolicyIssueEnum } from "@design-controls/types/src/openIssue";
import { QBox, QStack, QTag, QText, QTooltip } from "@qualio/ui-components";
import React from "react";
import { useTranslation } from "react-i18next";
import { useCurrentConfigs } from "../../hooks/useCurrentConfigs";

type Props = {
  openIssues: Partial<Record<PolicyIssueEnum, boolean>>;
  type: ElementList["type"];
  onlyIssues?: boolean;
};

const validTypes: Partial<Record<ElementList["type"], PolicyIssueEnum[]>> = {
  req1: [
    PolicyIssueEnum.REQUIRES_PARENT,
    PolicyIssueEnum.REQUIRES_CHILD,
    PolicyIssueEnum.UNTESTED_REQUIREMENT,
  ],
  req2: [
    PolicyIssueEnum.REQUIRES_PARENT,
    PolicyIssueEnum.REQUIRES_CHILD,
    PolicyIssueEnum.UNTESTED_REQUIREMENT,
  ],
  req3: [
    PolicyIssueEnum.REQUIRES_PARENT,
    PolicyIssueEnum.REQUIRES_CHILD,
    PolicyIssueEnum.UNTESTED_REQUIREMENT,
  ],
  req4: [PolicyIssueEnum.REQUIRES_PARENT],
  testCase1: [
    PolicyIssueEnum.REQUIRES_PARENT,
    PolicyIssueEnum.FAILING_TEST,
    PolicyIssueEnum.OUTDATED_TESTRESULT,
  ],
  testCase2: [
    PolicyIssueEnum.REQUIRES_PARENT,
    PolicyIssueEnum.FAILING_TEST,
    PolicyIssueEnum.OUTDATED_TESTRESULT,
  ],
  testCase3: [
    PolicyIssueEnum.REQUIRES_PARENT,
    PolicyIssueEnum.FAILING_TEST,
    PolicyIssueEnum.OUTDATED_TESTRESULT,
  ],
  risk: [PolicyIssueEnum.UNMITIGATED],
};

const getTableCode = (
  t: (key: string) => string,
): Record<PolicyIssueEnum, string> => ({
  [PolicyIssueEnum.FAILING_TEST]: t("openIssues.codes.F"),
  [PolicyIssueEnum.REQUIRES_CHILD]: t("openIssues.codes.C"),
  [PolicyIssueEnum.REQUIRES_PARENT]: t("openIssues.codes.P"),
  [PolicyIssueEnum.UNTESTED_REQUIREMENT]: t("openIssues.codes.T"),
  [PolicyIssueEnum.UNMITIGATED]: t("openIssues.codes.M"),
  [PolicyIssueEnum.OUTDATED_TESTRESULT]: t("openIssues.codes.D"),
});

const requiresChild: Partial<Record<Policy["type"], string>> = {
  req1: "req2",
  req2: "req3",
  req3: "req4",
} as const;

const requirementToTestCase: Partial<Record<Policy["type"], string>> = {
  req1: "testCase1",
  req2: "testCase2",
  req3: "testCase3",
} as const;

const requiresParent: Partial<Record<Policy["type"], string>> = {
  req2: "req1",
  req3: "req2",
  req4: "req3",
  testCase1: "req1",
  testCase2: "req2",
  testCase3: "req3",
} as const;

const openIssueTypeText = (
  policyIssue: PolicyIssueEnum,
  type: Policy["type"],
  policies: Policy[],
  hasGap: boolean,
  t: any,
) => {
  switch (policyIssue) {
    case "UNTESTED_REQUIREMENT":
      return hasGap
        ? t("openIssues.types.missingTestCase")
        : t("openIssues.types.requirementTested");
    case "UNMITIGATED":
      return hasGap
        ? t("openIssues.types.missingMitigation")
        : t("openIssues.types.mitigatedRisk");
    case "REQUIRES_CHILD": {
      const label =
        policies.find((policy) => policy.type === requiresChild[type])?.label ??
        "";
      return `${
        hasGap ? t("openIssues.types.missing") : t("openIssues.types.has")
      } ${label}`;
    }
    case "REQUIRES_PARENT": {
      const label =
        policies.find((policy) => policy.type === requiresParent[type])
          ?.label ?? "";
      return `${
        hasGap ? t("openIssues.types.missing") : t("openIssues.types.has")
      } ${label}`;
    }
    case "FAILING_TEST":
      return hasGap
        ? t("openIssues.types.failingTest")
        : t("openIssues.types.testPassed");
    case "OUTDATED_TESTRESULT":
      return hasGap
        ? t("openIssues.types.outdatedTestResult")
        : t("openIssues.types.upToDateResult");
    default:
      return "";
  }
};

const disableLabel = (
  key: string,
  type: ElementList["type"],
  configs: Policy[],
): boolean => {
  if (type === "req1" && key === PolicyIssueEnum.REQUIRES_PARENT) {
    return true;
  }

  if (
    key === PolicyIssueEnum.REQUIRES_CHILD &&
    !configs.find((item) => item.type === requiresChild[type])
  ) {
    return true;
  }

  if (
    key === PolicyIssueEnum.UNTESTED_REQUIREMENT &&
    !configs.find((item) => item.type === requirementToTestCase[type])
  ) {
    return true;
  }

  return false;
};

export const GapTableCell: React.FC<Props> = ({
  openIssues,
  type,
  onlyIssues,
}) => {
  const { t } = useTranslation();
  const policies = useCurrentConfigs();
  const tableCode = getTableCode(t);

  const tags = Object.entries(openIssues)
    .filter(([key]) => validTypes[type]?.includes(key as PolicyIssueEnum))
    .sort(
      (a, b) =>
        (validTypes[type] ?? []).findIndex((aType) => aType == a[0]) -
        (validTypes[type] ?? []).findIndex((bType) => bType == b[0]),
    );

  return (
    <QStack direction="row">
      {tags.map(([key, value]) => {
        if (!onlyIssues && disableLabel(key, type, policies)) {
          return (
            <div style={{ visibility: "hidden" }} key={`${key}-${type}`}>
              <QTag variantColor="green" data-cy={`${key}-ok`}>
                {tableCode[key as PolicyIssueEnum]}
              </QTag>
            </div>
          );
        }

        if (!!value) {
          return (
            <QBox key={`${key}-${type}`}>
              <QTooltip
                key={`${key}-${type}`}
                label={
                  <QText>
                    {openIssueTypeText(
                      key as PolicyIssueEnum,
                      type,
                      policies,
                      value,
                      t,
                    )}
                  </QText>
                }
              >
                <QTag variantColor="yellow" data-cy={`${key}-error`}>
                  {tableCode[key as PolicyIssueEnum]}
                </QTag>
              </QTooltip>
            </QBox>
          );
        }

        if (!onlyIssues) {
          return (
            <QBox>
              <QTooltip
                key={`${key}-${type}`}
                label={
                  <QText>
                    {openIssueTypeText(
                      key as PolicyIssueEnum,
                      type,
                      policies,
                      value,
                      t,
                    )}
                  </QText>
                }
              >
                <QTag variantColor="green" data-cy={`${key}-ok`}>
                  {tableCode[key as PolicyIssueEnum]}
                </QTag>
              </QTooltip>
            </QBox>
          );
        }

        return null;
      })}
    </QStack>
  );
};
